import { QueryClientProvider } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { AuthGate, type SessionStatus } from "./AuthGate.js";
import { ShellErrorBoundary, type ShellErrorBoundaryFallback } from "./ShellErrorBoundary.js";
import { ShellStoreProvider } from "./ShellStoreProvider.js";
import type { ShellStoreApi, WorkShapes } from "./session.js";

/**
 * `P0-UIK-013`: 4.6.4's composition, written once.
 *
 * *"Every app uses the same shell composition: `<AuthGate>` then
 * `<ShellStoreProvider>` then `<Layout>` then the router outlet. A screen
 * component is always a leaf."*
 *
 * **Why the ORDER is the deliverable.** Each of these four could be assembled
 * per app, and each app would get one of them subtly wrong in a way that never
 * looks wrong. Put the store above the gate and an anonymous visitor gets a
 * hydrated store belonging to whoever used the handset last. Put the boundary
 * below the layout and a crash takes the navigation with it, leaving the blank
 * page 4.6.6 forbids. Put the query client below the gate and the sign-in
 * screen cannot ask who the caller is. The order encodes those four answers,
 * and a package is how three apps get the same four.
 *
 * So, outermost to innermost: the query client (needed even to resolve the
 * session), the error boundary (must survive whatever it wraps), the auth
 * gate, the store, then the app's own `Layout` and outlet.
 *
 * **It renders no chrome.** `layout` is a function the app supplies, per
 * `DECISION ADDED A35`: apps own their look, and a shared header is how six
 * apps stop being six products.
 */

export interface ShellProps<Persona extends string, W extends WorkShapes> {
  readonly queryClient: QueryClient;
  readonly store: ShellStoreApi<Persona, W>;
  readonly status: SessionStatus;
  readonly pending: ReactNode;
  readonly signIn: ReactNode;
  readonly isPublicRoute?: boolean;
  /** The app's chrome, wrapping the router outlet. The shell supplies neither. */
  readonly layout: (outlet: ReactNode) => ReactNode;
  /** The app's error screen, built from `packages/ui`'s `ErrorScreen` with its own catalogue. */
  readonly renderError: (fallback: ShellErrorBoundaryFallback) => ReactNode;
  readonly crashMessage: string;
  /** The router outlet: whatever the app's router renders for the current route. */
  readonly children: ReactNode;
}

export function Shell<Persona extends string, W extends WorkShapes>({
  queryClient,
  store,
  status,
  pending,
  signIn,
  isPublicRoute = false,
  layout,
  renderError,
  crashMessage,
  children,
}: ShellProps<Persona, W>): React.JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      {/**
       * `OD-20`, closed at `round F5`: **TWO boundaries, and the inner one is
       * why the rail survives.**
       *
       * There was one, out here, wrapping the chrome as well as the outlet. It
       * caught a screen that threw , which is `P0-UIK-013` working, and no
       * blank page , and it unmounted the layout with it: the rail went, the
       * dealership's name went, and « Déconnexion » went. `00-SPINE` 6.2 says
       * every surface is one a person can LEAVE, and the moment that matters
       * most is the moment something has just broken. A person met an error
       * screen with no way off it but the browser's own back button.
       *
       * So the outlet gets its own boundary INSIDE `layout`. A screen that
       * throws is replaced where it stands; the chrome around it never
       * unmounts, because the failure never reaches it. The outer one stays for
       * what only it can catch , the layout itself throwing, the store, the
       * auth gate , where there is no chrome left to preserve and the whole
       * screen is the honest fallback.
       *
       * Both are the same component with the same words: an error screen that
       * looked different depending on which boundary caught it would be telling
       * a person something about this codebase's internals.
       */}
      <ShellErrorBoundary render={renderError} crashMessage={crashMessage}>
        <AuthGate status={status} pending={pending} signIn={signIn} isPublicRoute={isPublicRoute}>
          <ShellStoreProvider store={store}>
            {layout(
              <ShellErrorBoundary render={renderError} crashMessage={crashMessage}>
                {children}
              </ShellErrorBoundary>,
            )}
          </ShellStoreProvider>
        </AuthGate>
      </ShellErrorBoundary>
    </QueryClientProvider>
  );
}
